import { useState, useEffect, useCallback } from "react";
import { AlertCircle, AlertTriangle, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { SECTOARE, SUBSECTOARE } from "@/constants";
import { ServiceInfoModal } from "@/components/steps/ServiceInfoModal";
import { ServiceCard } from "@/components/onboarding/formular-notificare/ServiceCard";
import { SelectedServicesSummary } from "@/components/onboarding/formular-notificare/SelectedServicesSummary";
import type { EntityData } from "@/types/entity";
import type { SelectedService } from "@/types/service";

type LookupService = {
  id: string;
  name: string;
  category_id: string;
  category_name: string;
  sector_id: string;
  sector_name: string;
  subsector_id: string | null;
  subsector_name: string | null;
  entity_id: string;
  descriere_juridica: string | null;
  descriere_practica: string | null;
  nota_descriere: string | null;
};

type ServiceSelectionSectionProps = {
  entityData: Partial<EntityData>;
  selectedServices: SelectedService[];
  onServicesChange: (services: SelectedService[]) => void;
  fieldErrors: Record<string, string>;
};

const CLEAR_VALUE = "__clear__";

function getAvailableSubsectors(sectorKey: string) {
  return Object.entries(SUBSECTOARE).filter(([key]) =>
    key.startsWith(sectorKey)
  );
}

function ServiceSelectionSection({
  entityData,
  selectedServices,
  onServicesChange,
  fieldErrors,
}: ServiceSelectionSectionProps) {
  const [allServices, setAllServices] = useState<LookupService[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedSector, setSelectedSector] = useState("");
  const [selectedSubsector, setSelectedSubsector] = useState("");
  const [infoModalService, setInfoModalService] =
    useState<LookupService | null>(null);

  const hasCaenData = Boolean(entityData.caen_principal?.cod);

  useEffect(() => {
    if (!hasCaenData) return;

    const loadServices = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const { data, error } = await supabase.functions.invoke(
          "api/get-lookup-services",
          { method: "GET" }
        );

        if (error) throw error;

        const services = (data?.services || data || []) as LookupService[];
        setAllServices(services);
      } catch {
        setLoadError("Eroare la încărcarea serviciilor. Reîncearcă.");
      } finally {
        setIsLoading(false);
      }
    };

    loadServices();
  }, [hasCaenData]);

  const handleSectorChange = useCallback((value: string) => {
    setSelectedSector(value === CLEAR_VALUE ? "" : value);
    setSelectedSubsector("");
  }, []);

  const handleSubsectorChange = useCallback((value: string) => {
    setSelectedSubsector(value === CLEAR_VALUE ? "" : value);
  }, []);

  const filteredServices = allServices.filter((service) => {
    if (!selectedSector) return true;
    const sectorName = SECTOARE[selectedSector as keyof typeof SECTOARE];
    if (!sectorName) return true;
    const matchesSector = service.sector_name === sectorName;

    if (!selectedSubsector) return matchesSector;
    const subsectorName =
      SUBSECTOARE[selectedSubsector as keyof typeof SUBSECTOARE];
    if (!subsectorName) return matchesSector;

    return matchesSector && service.subsector_name === subsectorName;
  });

  const annexa1Services = filteredServices.filter((s) => s.category_id === "1");
  const annexa2Services = filteredServices.filter((s) => s.category_id === "2");

  const selectedIds = new Set(
    selectedServices.map((s) => `${s.category_id}-${s.name}`)
  );

  const annexa1SelectedCount = selectedServices.filter(
    (s) => s.category_id === "1"
  ).length;
  const annexa2SelectedCount = selectedServices.filter(
    (s) => s.category_id === "2"
  ).length;

  const removeService = useCallback(
    (service: SelectedService) => {
      const serviceKey = `${service.category_id}-${service.name}`;
      onServicesChange(
        selectedServices.filter(
          (s) => `${s.category_id}-${s.name}` !== serviceKey
        )
      );
    },
    [selectedServices, onServicesChange]
  );

  const toggleService = useCallback(
    (service: LookupService) => {
      const serviceKey = `${service.category_id}-${service.name}`;
      const isSelected = selectedIds.has(serviceKey);

      if (isSelected) {
        onServicesChange(
          selectedServices.filter(
            (s) => `${s.category_id}-${s.name}` !== serviceKey
          )
        );
      } else {
        const newService: SelectedService = {
          entity_id: service.entity_id,
          category_id: service.category_id,
          sector_name: service.sector_name,
          subsector_name: service.subsector_name,
          name: service.name,
        };
        onServicesChange([...selectedServices, newService]);
      }
    },
    [selectedServices, selectedIds, onServicesChange]
  );

  if (!hasCaenData) {
    return (
      <div className="text-center py-8 text-ds-text-400">
        <AlertCircle className="w-10 h-10 mx-auto mb-3 text-ds-border-300" />
        <p className="text-sm font-medium">
          Completează CUI-ul și datele companiei mai întâi
        </p>
        <p className="text-xs mt-1">
          Codul CAEN este necesar pentru a identifica serviciile NIS2
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 gap-3 text-ds-text-500">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="text-sm">Se încarcă serviciile...</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="text-center py-8 text-ds-red-500">
        <AlertCircle className="w-10 h-10 mx-auto mb-3" />
        <p className="text-sm">{loadError}</p>
      </div>
    );
  }

  const availableSubsectors = selectedSector
    ? getAvailableSubsectors(selectedSector)
    : [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-ds-text-400">
          Selectează serviciile NIS2 relevante pentru compania ta
        </p>
        <Badge variant="outline" className="text-xs">
          {selectedServices.length} servicii selectate
        </Badge>
      </div>

      {fieldErrors["selectedServices"] && (
        <div className="flex items-center gap-2 p-3 bg-destructive/5 border border-destructive/20 rounded-lg">
          <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0" />
          <span className="text-xs text-destructive">
            {fieldErrors["selectedServices"]}
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="sector-filter" className="text-xs">
            Filtrează după sector
          </Label>
          <Select
            value={selectedSector || CLEAR_VALUE}
            onValueChange={handleSectorChange}
          >
            <SelectTrigger id="sector-filter" className="text-xs md:text-sm">
              <SelectValue placeholder="Toate sectoarele" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={CLEAR_VALUE}>Toate sectoarele</SelectItem>
              {Object.entries(SECTOARE).map(([key, name]) => (
                <SelectItem key={key} value={key}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="subsector-filter" className="text-xs">
            Filtrează după subsector
          </Label>
          <Select
            value={selectedSubsector || CLEAR_VALUE}
            onValueChange={handleSubsectorChange}
            disabled={!selectedSector || availableSubsectors.length === 0}
          >
            <SelectTrigger id="subsector-filter" className="text-xs md:text-sm">
              <SelectValue placeholder="Toate subsectoarele" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={CLEAR_VALUE}>Toate subsectoarele</SelectItem>
              {availableSubsectors.map(([key, name]) => (
                <SelectItem key={key} value={key}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="annexa1" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger
            value="annexa1"
            className="group text-xs md:text-sm data-[state=active]:bg-ds-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md"
          >
            Anexa I (Esențiale)
            {annexa1SelectedCount > 0 && (
              <Badge className="ml-2 bg-ds-blue-100 text-ds-blue-700 group-data-[state=active]:bg-ds-blue-500 group-data-[state=active]:text-white text-xs">
                {annexa1SelectedCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="annexa2"
            className="group text-xs md:text-sm data-[state=active]:bg-ds-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md"
          >
            Anexa II (Importante)
            {annexa2SelectedCount > 0 && (
              <Badge className="ml-2 bg-ds-blue-100 text-ds-blue-700 group-data-[state=active]:bg-ds-blue-500 group-data-[state=active]:text-white text-xs">
                {annexa2SelectedCount}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent
          value="annexa1"
          className="mt-4 max-h-[500px] overflow-y-auto"
        >
          <ServiceGrid
            services={annexa1Services}
            selectedIds={selectedIds}
            onToggle={toggleService}
            onInfoClick={setInfoModalService}
          />
        </TabsContent>

        <TabsContent value="annexa2" className="mt-4">
          <ServiceGrid
            services={annexa2Services}
            selectedIds={selectedIds}
            onToggle={toggleService}
            onInfoClick={setInfoModalService}
          />
        </TabsContent>
      </Tabs>

      <SelectedServicesSummary
        selectedServices={selectedServices}
        collapsible
        onRemove={removeService}
      />

      <ServiceInfoModal
        open={!!infoModalService}
        onOpenChange={(open) => {
          if (!open) setInfoModalService(null);
        }}
        service={
          infoModalService
            ? {
              name: infoModalService.name,
              subsector_name: infoModalService.subsector_name,
              category_id: infoModalService.category_id,
              descriere_juridica: infoModalService.descriere_juridica,
              descriere_practica: infoModalService.descriere_practica,
              note_DNSC: infoModalService.nota_descriere || "",
            }
            : null
        }
      />
    </div>
  );
}

function ServiceGrid({
  services,
  selectedIds,
  onToggle,
  onInfoClick,
}: {
  services: LookupService[];
  selectedIds: Set<string>;
  onToggle: (service: LookupService) => void;
  onInfoClick: (service: LookupService) => void;
}) {
  if (services.length === 0) {
    return (
      <p className="text-sm text-ds-text-400 text-center py-6">
        Nu au fost găsite servicii pentru filtrele selectate.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3">
      {services.map((service) => (
        <ServiceCard
          key={service.id}
          name={service.name}
          subsectorName={service.subsector_name}
          categoryId={service.category_id}
          isSelected={selectedIds.has(`${service.category_id}-${service.name}`)}
          onToggle={() => onToggle(service)}
          onInfoClick={() => onInfoClick(service)}
        />
      ))}
    </div>
  );
}

export default ServiceSelectionSection;
